import { z } from "zod";
import { REPORT_TYPES } from "./enums.ts";

export const Choice = z.enum(["A", "B", "C", "D"]);
export const Part = z.number().int().min(1).max(7);
export const AttemptInput = z.strictObject({
  testId: z.uuid(),
  mode: z.enum(["exam", "practice"]),
  parts: z.array(Part).min(1).max(7),
  timeLimitSec: z.number().int().min(60).max(14400).nullable().optional(),
  immediate: z.boolean().default(false),
});
export const DrillInput = z.strictObject({
  poolKey: z.enum(["1", "2", "3", "4", "5", "6", "7s", "7d", "7t"]),
  level: z.number().int().min(1).max(5),
});
export const AttemptState = z.strictObject({
  screen: z.number().int().min(0).max(500).default(0),
  flagged: z.array(z.uuid()).max(300).default([]),
});
export const AnswerInput = z.strictObject({
  expectedRev: z.number().int().min(0),
  answers: z
    .array(
      z.strictObject({
        questionId: z.uuid(),
        chosen: Choice.nullable(),
        timeSpentMs: z.number().int().min(0).max(14400000).default(0),
      }),
    )
    .min(1)
    .max(300),
});
export const StateInput = z.strictObject({
  expectedRev: z.number().int().min(0),
  state: AttemptState.optional(),
  action: z.enum(["pause", "resume", "abandon"]).optional(),
});
export const RevInput = z.strictObject({ expectedRev: z.number().int().min(0) });
export const GradeInput = z.strictObject({
  expectedRev: z.number().int().min(0),
  questionIds: z.array(z.uuid()).min(1).max(10),
});
export const Result = z.strictObject({
  correct: z.number().int(),
  total: z.number().int(),
  lcCorrect: z.number().int(),
  rcCorrect: z.number().int(),
  lcScore: z.number().int().nullable(),
  rcScore: z.number().int().nullable(),
  totalScore: z.number().int().nullable(),
  activeSecs: z.number().int(),
  parts: z.array(
    z.strictObject({ part: Part, correct: z.number().int(), total: z.number().int() }),
  ),
});
export const Attempt = z.strictObject({
  id: z.uuid(),
  testId: z.uuid().nullable(),
  contentVersionId: z.uuid().nullable(),
  name: z.string(),
  kind: z.string(),
  mode: z.enum(["exam", "practice"]),
  parts: z.array(Part),
  immediate: z.boolean(),
  status: z.enum(["in_progress", "submitted", "abandoned"]),
  rev: z.number().int(),
  startedAt: z.string(),
  serverNow: z.string(),
  deadlineAt: z.string().nullable(),
  listeningDeadlineAt: z.string().nullable().default(null),
  pausedAt: z.string().nullable(),
  remainingSec: z.number().nullable(),
  submittedAt: z.string().nullable(),
  state: AttemptState,
  answers: z.array(
    z.strictObject({
      questionId: z.uuid(),
      chosen: Choice.nullable(),
      timeSpentMs: z.number().int(),
      graded: z.boolean(),
    }),
  ),
  result: Result.nullable(),
});
export type Attempt = z.infer<typeof Attempt>;
export const TestSummary = z.strictObject({
  id: z.uuid(),
  code: z.string(),
  name: z.string(),
  kind: z.string(),
  setCode: z.string(),
  setName: z.string(),
  parts: z.array(Part),
  durationMin: z.number().int(),
  questionCount: z.number().int(),
  activeAttemptId: z.uuid().nullable(),
  lastScore: z.number().nullable(),
  lastCorrect: z.number().nullable(),
  completed: z.boolean(),
});
export const Pool = z.strictObject({
  poolKey: z.string(),
  part: Part,
  level: z.number().int(),
  total: z.number().int(),
  remaining: z.number().int(),
  locked: z.boolean(),
});
export const Score = z.strictObject({
  id: z.uuid(),
  name: z.string(),
  submittedAt: z.string(),
  result: Result,
});
export const ReportInput = z.strictObject({
  attemptId: z.uuid(),
  questionId: z.uuid(),
  type: z.enum(REPORT_TYPES),
  message: z.string().trim().min(5).max(2000),
});

/** Same weighted distribution as the old scoreboard, with explicit bounds. */
export function distributeTarget(counts: number[], weights: number[], target: number): number[] {
  const capacity = counts.reduce((a, b) => a + b, 0);
  target = Math.max(0, Math.min(capacity, Math.round(target)));
  const raw = counts.map((n, i) =>
    Math.min(n, (n * (weights[i] ?? 1) * target) / Math.max(1, capacity)),
  );
  const out = raw.map(Math.floor);
  let sum = out.reduce((a, b) => a + b, 0);
  while (sum !== target) {
    const up = sum < target;
    let best = -1,
      delta = up ? -Infinity : Infinity;
    out.forEach((n, i) => {
      const d = (raw[i] ?? 0) - n;
      if (up ? n < (counts[i] ?? 0) && d > delta : n > 0 && d < delta) {
        best = i;
        delta = d;
      }
    });
    if (best < 0) break;
    out[best] = (out[best] ?? 0) + (up ? 1 : -1);
    sum += up ? 1 : -1;
  }
  return out;
}
export function scoreTargets(goal: number, lc: number[], rc: number[]) {
  goal = Math.max(10, Math.min(990, Math.round(goal || 600)));
  let best = { lc: 100, rc: 100, score: 990, penalty: Infinity };
  for (let l = 0; l <= 100; l++) {
    const r = rc.findIndex((s) => s + (lc[l] ?? 5) >= goal);
    if (r < 0) continue;
    const score = (lc[l] ?? 5) + (rc[r] ?? 5);
    const penalty = Math.abs(l - r - 8) * 10 + score - goal;
    if (penalty < best.penalty) best = { lc: l, rc: r, score, penalty };
  }
  const listening = distributeTarget([6, 25, 39, 30], [1.042, 1, 0.994, 1], best.lc);
  const reading = distributeTarget([30, 16, 29, 25], [1.111, 1.042, 1.006, 0.833], best.rc);
  return {
    goal,
    lc: best.lc,
    rc: best.rc,
    estimatedScore: best.score,
    parts: [...listening, reading[0] ?? 0, reading[1] ?? 0, (reading[2] ?? 0) + (reading[3] ?? 0)],
    /** Part 7 tách theo số đoạn của bài đọc: đoạn đơn (29 câu) và đoạn đôi, ba (25 câu). */
    part7: { single: reading[2] ?? 0, multi: reading[3] ?? 0 },
  };
}

export type AttemptInput = z.infer<typeof AttemptInput>;
export type AnswerInput = z.infer<typeof AnswerInput>;
export type StateInput = z.infer<typeof StateInput>;
export type DrillInput = z.infer<typeof DrillInput>;
